import React, { useState, useEffect } from 'react';
import { Box, TextField, Button } from '@mui/material';
import Text from '../components/atoms/Text';
import { classes } from '../styles/WaitlistStyle';
import config from '../config';

// Import assets
import blueCheckIcon from '../components/icons/blue-check.svg';
import logo from '../components/images/logo.svg';
import screenshot from '../components/images/waitlist-screenshot.jpg';

function Waitlist() {
  const [tiltTransform, setTiltTransform] = useState(
    'perspective(800px) rotateX(0deg) rotateY(0deg)'
  );
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // This handler will run for every mouse movement in the window
    function handleMouseMove(e) {
      // If the screen is below 900px wide, either disable or reduce the tilt
      if (window.innerWidth < 900) {
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        const rotateX = ((centerY - e.clientY) / 200).toFixed(2);
        const rotateY = ((e.clientX - centerX) / 200).toFixed(2);
        setTiltTransform(
          `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
        );
        return;
      }

      // Otherwise, normal tilt for larger screens
      const centerX = window.innerWidth / 2;
      const centerY = window.innerHeight / 2;
      const rotateX = ((centerY - e.clientY) / 80).toFixed(2);
      const rotateY = ((e.clientX - centerX) / 80).toFixed(2);
      setTiltTransform(
        `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
      );
    }

    window.addEventListener('mousemove', handleMouseMove);

    // Cleanup: remove listener on unmount
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Basic email validation using a regex
  const validateEmail = (email) => {
    const regex = /^\S+@\S+\.\S+$/;
    return regex.test(email);
  };

  // Handler for submitting the email to the waitlist
  const handleJoinWaitlist = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate the email before submitting
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setLoading(false);
      return;
    } else {
      setEmailError('');
    }

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Something went wrong. Please try again.');
      }

      setSuccess(
        'Thank you for joining the waitlist! Please check your email for confirmation from IntenceAI.'
      );
      setEmail(''); // Clear the input field after successful submission
    } catch (err) {
      setError(err.message || 'Error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={classes.waitlistContainer}>
      {/* Logo / Nav */}
      <Box sx={classes.logoContainer}>
        <Box
          component="img"
          src={logo}
          alt="IntenceAI Logo"
          sx={classes.logo}
        />
      </Box>

      {/* Hero Section */}
      <Box sx={classes.heroSection}>
        {/* Left Column: App Screenshot (transformed by tiltTransform) */}
        <Box sx={classes.leftColumn}>
          <Box sx={classes.backgroundBlueCircle} />
          <Box
            component="img"
            src={screenshot}
            alt="Screenshot of IntenceAI Dashboard"
            sx={{
              ...classes.screenshotImage,
              transform: tiltTransform,
            }}
          />
        </Box>

        {/* Right Column: Headline, Bullets, Email Form */}
        <Box sx={classes.rightColumn}>
          {/* Badge */}
          <Box sx={classes.badge}>
            <Text variant="caption" sx={classes.badgeText}>
              TWITTER AI AGENT
            </Text>
          </Box>

          {/* Title */}
          <Text variant="h1" sx={classes.title}>
            Put your <span style={{ color: '#4ABFFD' }}>Twitter</span> on
            auto-pilot
          </Text>

          {/* Subtitle / Paragraph */}
          <Text variant="body1" sx={classes.subtitle}>
            Relax as our AI agent generates, schedules, optimizes, and grows
            your Twitter presence all on its own. Set up your account once, then
            leave the rest to us.
          </Text>

          {/* Feature List */}
          <Box component="ul" sx={classes.featureList}>
            <Box component="li" sx={classes.featureItem}>
              <Box
                component="img"
                src={blueCheckIcon}
                alt="Checkmark icon"
                sx={classes.checkIcon}
              />
              <Text variant="body1" sx={classes.listItem}>
                Auto posts engaging tweets tailored to your brand
              </Text>
            </Box>
            <Box component="li" sx={classes.featureItem}>
              <Box
                component="img"
                src={blueCheckIcon}
                alt="Checkmark icon"
                sx={classes.checkIcon}
              />
              <Text variant="body1" sx={classes.listItem}>
                Discovers trending topics and viral content to post
              </Text>
            </Box>
            <Box component="li" sx={classes.featureItem}>
              <Box
                component="img"
                src={blueCheckIcon}
                alt="Checkmark icon"
                sx={classes.checkIcon}
              />
              <Text variant="body1" sx={classes.listItem}>
                Analyzes and improves its own strategy over time
              </Text>
            </Box>
          </Box>

          {/* Email Input + Button */}
          <Box sx={classes.waitlistForm}>
            <TextField
              placeholder="Enter your email here"
              variant="outlined"
              type="email"
              sx={classes.emailInput}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(''); // Clear error as user types
              }}
              error={Boolean(emailError)}
              helperText={emailError}
              disabled={loading || success}
            />
            <Button
              variant="contained"
              sx={classes.joinButton}
              onClick={handleJoinWaitlist}
              disabled={loading || !email}
            >
              {loading ? 'Joining...' : 'JOIN WAITLIST'}
            </Button>
          </Box>

          {/* Display success or error messages */}
          {error && (
            <Text variant="body2" sx={{ color: 'red', mt: 1 }}>
              {error}
            </Text>
          )}
          {success && (
            <Text variant="body2" sx={{ color: 'green', mt: 1 }}>
              {success}
            </Text>
          )}

          {/* Small Note */}
          <Text variant="body2" sx={classes.footnote}>
            ✨ Psst...early signups will get access to the free tier!
          </Text>
        </Box>
      </Box>
    </Box>
  );
}

export default Waitlist;
